import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Breadcrumbs,
  Link,
  useTheme,
} from '@mui/material';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search } from '@mui/icons-material';
import PostList from '../../components/blog/PostList';
import PostSidebar from '../../components/blog/PostSidebar';
import { useGetPosts } from '../../hooks/storefront/useGetPosts';
import { useGetPostCategories } from '../../hooks/storefront/useGetPostCategories';
import { useGetPostTags } from '../../hooks/storefront/useGetPostTags';
import { useGetRecentPosts } from '../../hooks/storefront/useGetRecentPosts';
import { useGetFeaturedPosts } from '../../hooks/storefront/useGetFeaturedPosts';

const Blog = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [page, setPage] = useState(1);

  // Get query parameters from URL
  const categorySlug = searchParams.get('category');
  const tagSlug = searchParams.get('tag');
  const searchQuery = searchParams.get('search');

  // Update filters when URL params change
  useEffect(() => {
    setSearchTerm(searchQuery || '');
    setSelectedCategory(categorySlug || '');
    setSelectedTag(tagSlug || '');
    setPage(1);
  }, [categorySlug, tagSlug, searchQuery]);

  // Build API parameters
  const apiParams = {
    page,
    per_page: 10,
    ...(searchTerm && { search: searchTerm }),
    ...(selectedCategory && { category_id: selectedCategory }),
    ...(selectedTag && { tag_id: selectedTag }),
  };

  // Fetch data
  const { posts, loading, error, meta } = useGetPosts(apiParams);
  const { categories } = useGetPostCategories();
  const { tags } = useGetPostTags();
  const { posts: recentPosts } = useGetRecentPosts({ limit: 5 });
  const { posts: featuredPosts } = useGetFeaturedPosts({ limit: 3 });

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    // Update URL
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.delete('page'); // Reset page when searching
    setSearchParams(newParams);
  };

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    setSelectedCategory(value);
    
    // Update URL
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('category', value);
    } else {
      newParams.delete('category');
    }
    newParams.delete('page'); // Reset page when filtering
    setSearchParams(newParams);
  };

  const handleTagChange = (event) => {
    const value = event.target.value;
    setSelectedTag(value);
    
    // Update URL
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('tag', value);
    } else {
      newParams.delete('tag');
    }
    newParams.delete('page'); // Reset page when filtering
    setSearchParams(newParams);
  };

  const handleLoadMore = () => {
    if (meta && page < meta.last_page) {
      setPage(page + 1);
      // Update URL
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', (page + 1).toString());
      setSearchParams(newParams);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedTag('');
    setPage(1);
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = searchTerm || selectedCategory || selectedTag;

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: { xs: 3, md: 5 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/" color="inherit">
            {t('common.home', 'Home')}
          </Link>
          <Typography color="text.primary">
            {t('blog.blog', 'Blog')}
          </Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 6,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(255, 215, 0, 0.05) 0%, rgba(255, 215, 0, 0.02) 100%)',
          borderRadius: 3,
          py: 4,
          px: 2,
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 215, 0, 0.2)' : 'rgba(255, 215, 0, 0.1)'}`,
        }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)'
                : 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('blog.blogTitle', 'Our Blog')}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            {t('blog.blogDescription', 'Discover the latest trends, tips, and insights in our industry')}
          </Typography>
        </Box>

        {/* Filters */}
        <Box sx={{ 
          mb: 4,
          background: theme.palette.mode === 'dark'
            ? 'rgba(26, 26, 26, 0.95)'
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          p: 3,
          border: `1px solid ${theme.palette.divider}`,
        }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder={t('blog.searchPlaceholder', 'Search posts...')}
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            {/* Category Filter */}
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('blog.category', 'Category')}</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  label={t('blog.category', 'Category')}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <MenuItem value="">{t('blog.allCategories', 'All Categories')}</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.slug}>
                      {category.name?.en || category.name?.ar}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Tag Filter */}
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('blog.tag', 'Tag')}</InputLabel>
                <Select
                  value={selectedTag}
                  onChange={handleTagChange}
                  label={t('blog.tag', 'Tag')}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <MenuItem value="">{t('blog.allTags', 'All Tags')}</MenuItem>
                  {tags.slice(0, 20).map((tag) => (
                    <MenuItem key={tag.id} value={tag.slug}>
                      {tag.name?.en || tag.name?.ar}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Clear Filters */}
            <Grid item xs={12} sm={4} md={2}>
              {hasActiveFilters && (
                <Box
                  onClick={clearFilters}
                  sx={{
                    px: 2,
                    py: 1,
                    border: `1px solid`,
                    borderColor: 'divider',
                    borderRadius: 2,
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {t('blog.clearFilters', 'Clear')}
                </Box>
              )}
            </Grid>
          </Grid>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              {searchTerm && (
                <Chip
                  label={`${t('blog.search', 'Search')}: ${searchTerm}`}
                  onDelete={() => handleSearch({ target: { value: '' } })}
                  size="small"
                  color="primary"
                />
              )}
              {selectedCategory && (
                <Chip
                  label={`${t('blog.category', 'Category')}: ${categories.find(c => c.slug === selectedCategory)?.name?.en || selectedCategory}`}
                  onDelete={() => handleCategoryChange({ target: { value: '' } })}
                  size="small"
                  color="primary"
                />
              )}
              {selectedTag && (
                <Chip
                  label={`${t('blog.tag', 'Tag')}: ${tags.find(t => t.slug === selectedTag)?.name?.en || selectedTag}`}
                  onDelete={() => handleTagChange({ target: { value: '' } })}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
          )}
        </Box>

        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} lg={8}>
            <PostList
              posts={posts}
              loading={loading}
              error={error}
              columns={2}
              showLoadMore={meta && page < meta.last_page}
              onLoadMore={handleLoadMore}
              loadingMore={loading && page > 1}
            />
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            <PostSidebar
              categories={categories}
              tags={tags}
              recentPosts={recentPosts}
              featuredPosts={featuredPosts}
            />
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Blog;
